import React, { useEffect, useState } from "react";
import {
  getInvoiceById,
  updatePayment,
} from "../../services/cateringInvoiceService";
import { useParams } from "react-router-dom";
import { toast } from "react-hot-toast";

const InvoiceDetails = () => {
  const { id } = useParams();
  const [invoice, setInvoice] = useState(null);
  const [loading, setLoading] = useState(false);
  const [updatingPayment, setUpdatingPayment] = useState(false);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [newPaidAmountInput, setNewPaidAmountInput] = useState("");

  const fetchInvoice = async () => {
    setLoading(true);
    try {
      const data = await getInvoiceById(id);
      setInvoice(data);
    } catch (err) {
      console.error("Error fetching invoice:", err.message || err);
      toast.error("Failed to fetch invoice data");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchInvoice();
  }, [id]);

  const handlePaymentUpdate = async (newPaidAmount) => {
    if (isNaN(newPaidAmount) || newPaidAmount < 0) {
      toast.error("Please enter a valid positive number");
      return;
    }
    setUpdatingPayment(true);
    try {
      await updatePayment(id, newPaidAmount);
      await fetchInvoice(); // <-- Reload invoice after payment update
    } catch (err) {
      console.error("Payment update failed:", err.message || err);
      toast.error("Failed to update payment");
    } finally {
      setUpdatingPayment(false);
    }
  };

  if (loading || !invoice) {
    return <div className="p-4 text-center">Loading invoice...</div>;
  }

  const customer = invoice.CateringCustomer || {};
  const items = invoice.CateringInvoiceItems || {};

  // Format date to more readable form (e.g., 10 Jun 2025)
  const formattedEventDate = new Date(invoice.eventDate).toLocaleDateString(
    "en-IN",
    {
      day: "numeric",
      month: "short",
      year: "numeric",
    }
  );

  return (
    <div className="p-4 max-w-3xl mx-auto bg-white shadow rounded">
      <h2 className="text-2xl font-bold mb-6 text-center">Invoice Details</h2>

      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mb-6 text-sm sm:text-base">
        <div>
          <span className="font-semibold">Customer:</span>{" "}
          {customer.name || "N/A"}
        </div>
        <div>
          <span className="font-semibold">Mobile:</span>{" "}
          {customer.mobile || "N/A"}
        </div>
        <div>
          <span className="font-semibold">Address:</span>{" "}
          {customer.address || "N/A"}
        </div>
        <div>
          <span className="font-semibold">Event Date:</span>{" "}
          {formattedEventDate}
        </div>
        <div>
          <span className="font-semibold">Budget:</span> ₹{invoice.budget}
        </div>
        <div>
          <span className="font-semibold">Paid:</span> ₹{invoice.paidAmount}
        </div>
        <div>
          <span className="font-semibold">Due:</span> ₹{invoice.dueAmount}
        </div>
        <div>
          <span className="font-semibold">Status:</span> {invoice.status}
        </div>
      </div>

      <h3 className="text-lg font-semibold mb-3">Items</h3>

      <div className="overflow-x-auto">
        <table className="min-w-full border border-gray-300 text-sm sm:text-base">
          <thead className="bg-gray-100">
            <tr>
              <th className="border px-3 py-2 text-left whitespace-nowrap">
                #
              </th>
              <th className="border px-3 py-2 text-left whitespace-nowrap">
                Item
              </th>
              <th className="border px-3 py-2 text-center whitespace-nowrap">
                Qty
              </th>
              <th className="border px-3 py-2 text-right whitespace-nowrap">
                Rate
              </th>
              <th className="border px-3 py-2 text-right whitespace-nowrap">
                Total
              </th>
              <th className="border px-3 py-2 text-left whitespace-nowrap">
                Remarks
              </th>
            </tr>
          </thead>
          <tbody>
            {items.map((item, idx) => (
              <tr
                key={item.id}
                className={idx % 2 === 0 ? "bg-white" : "bg-gray-50"}
              >
                <td className="border px-3 py-2 text-left">{idx + 1}</td>
                <td className="border px-3 py-2 text-left">{item.itemName}</td>
                <td className="border px-3 py-2 text-center">
                  {item.quantity}
                </td>
                <td className="border px-3 py-2 text-right">₹{item.rate}</td>
                <td className="border px-3 py-2 text-right">₹{item.total}</td>
                <td className="border px-3 py-2 text-left">
                  {item.remarks || "-"}
                </td>
              </tr>
            ))}
          </tbody>
          <tfoot>
            <tr className="bg-gray-200 font-semibold">
              <td colSpan={4} className="border px-3 py-2 text-right">
                Total Amount
              </td>
              <td className="border px-3 py-2 text-right">
                ₹{items.reduce((sum, item) => sum + Number(item.total), 0)}
              </td>
              <td className="border px-3 py-2"></td>
            </tr>
          </tfoot>
        </table>
      </div>

      <button
        onClick={() => {
          setNewPaidAmountInput(0 || 0); // set current paid amount when opening modal
          setIsModalOpen(true);
        }}
        disabled={updatingPayment}
        className="px-4 py-2 rounded bg-blue-600 text-white font-semibold hover:bg-blue-700 disabled:opacity-50"
      >
        {updatingPayment ? "Updating..." : "Update Paid Amount"}
      </button>

      {isModalOpen && (
        <div className="fixed inset-0 flex items-center justify-center bg-black bg-opacity-50 z-50">
          <div className="bg-white rounded-lg shadow-lg p-6 max-w-sm w-full">
            <h3 className="text-lg font-semibold mb-4">Update Paid Amount</h3>
            <input
              type="number"
              min="0"
              value={newPaidAmountInput}
              onChange={(e) => setNewPaidAmountInput(e.target.value)}
              className="w-full border rounded px-3 py-2 mb-4"
            />
            <div className="flex justify-end space-x-3">
              <button
                onClick={() => setIsModalOpen(false)}
                className="px-4 py-2 rounded bg-gray-300 hover:bg-gray-400"
              >
                Cancel
              </button>
              <button
                onClick={() => {
                  const num = Number(newPaidAmountInput);
                  if (isNaN(num) || num < 0) {
                    alert("Please enter a valid positive number");
                    return;
                  }
                  handlePaymentUpdate(num);
                  setIsModalOpen(false);
                }}
                disabled={updatingPayment}
                className="px-4 py-2 rounded bg-blue-600 text-white hover:bg-blue-700 disabled:opacity-50"
              >
                {updatingPayment ? "Updating..." : "Submit"}
              </button>
            </div>
          </div>
        </div>
      )}

      
    </div>
  );
};

export default InvoiceDetails;
